/* Native touch scrolling and per-card controls; no carousel dependency. */
if (!customElements.get('daziner-product-gallery')) {
  customElements.define('daziner-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('.dz-gallery__slide'));
      this.controls = this.querySelector('[data-gallery-controls]');
      if (!this.track || this.slides.length < 2 || !this.controls) return;
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.index = 0;
      this.previous = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.count = this.querySelector('[data-gallery-count]');
      this.controls.hidden = false;
      this.previous.addEventListener('click', () => this.go(this.index - 1), options);
      this.next.addEventListener('click', () => this.go(this.index + 1), options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (!this.dragging) this.update();
        });
      }, { ...options, passive: true });
      this.addEventListener('keydown', event => {
        const actions = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in actions)) return;
        event.preventDefault();
        this.track.focus({ preventScroll: true });
        this.go(actions[event.key]);
      }, options);
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        this.suppressClickUntil = 0;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.pointer = { id: event.pointerId, x: event.clientX, scroll: this.track.scrollLeft };
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        if (!(event.buttons & 1)) { this.pointer = null; return; }
        const distance = event.clientX - this.pointer.x;
        if (Math.abs(distance) > 8 && !this.dragging) {
          this.dragging = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        if (this.dragging) {
          event.preventDefault();
          this.track.scrollLeft = this.pointer.scroll - distance;
        }
      }, options);
      const finish = event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        this.pointer = null;
        if (!this.dragging) return;
        this.dragging = false;
        this.suppressClickUntil = Date.now() + 400;
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.go(Math.round(this.track.scrollLeft / this.track.clientWidth));
      };
      this.track.addEventListener('pointerup', finish, options);
      this.track.addEventListener('pointercancel', finish, options);
      this.track.addEventListener('lostpointercapture', finish, options);
      this.track.addEventListener('click', event => {
        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
      let width = this.track.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (width === this.track.clientWidth) return;
        width = this.track.clientWidth;
        this.track.scrollTo({ left: this.index * width, behavior: 'instant' });
      });
      this.resizeObserver.observe(this.track);
      this.imageObserver?.disconnect();
      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver(entries => {
          this.nearViewport = entries.some(entry => entry.isIntersecting);
          if (this.nearViewport) this.loadNearby(this.index);
        }, { rootMargin: '300px 0px' });
        this.imageObserver.observe(this);
      }
      this.update();
    }
    loadNearby(index) {
      for (let candidate = Math.max(0, index - 1); candidate <= Math.min(this.slides.length - 1, index + 1); candidate++) {
        const template = this.slides[candidate].querySelector('template[data-gallery-image]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      }
    }
    go(index) {
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      this.loadNearby(next);
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
    }
    update() {
      if (!this.track.clientWidth) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
      if (this.nearViewport) this.loadNearby(this.index);
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      const label = `${this.index + 1} / ${this.slides.length}`;
      if (this.count.textContent !== label) this.count.textContent = label;
      this.slides.forEach((slide, index) => { slide.tabIndex = index === this.index ? 0 : -1; });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.imageObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
